import {
  Building2,
  KeyRound,
  FileImage,
  AlertTriangle,
  CreditCard,
  BookOpen,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

interface SettingsNavItem {
  href: string;
  label: string;
  labelKey: string;
  description: string;
  descKey: string;
  icon: React.ElementType;
  badge?: string;
}

const settingsNavItems: SettingsNavItem[] = [
  {
    href: '/general',
    label: 'General',
    description: 'Organization name and URL',
    icon: Building2,
    labelKey: 'SIDEBAR_GENERAL_LABEL',
    descKey: 'SIDEBAR_GENERAL_DESCRIPTION',
  },
  {
    href: '/integrations',
    label: 'Integrations',
    description: 'SELISE Signature and APIs',
    icon: KeyRound,
    labelKey: 'SIDEBAR_INTEGRATIONS_LABEL',
    descKey: 'SIDEBAR_INTEGRATIONS_DESCRIPTION',
  },
  {
    href: '/letterheads',
    label: 'Letterheads',
    description: 'Company document headers',
    icon: FileImage,
    labelKey: 'SIDEBAR_LETTERHEADS_LABEL',
    descKey: 'SIDEBAR_LETTERHEADS_DESCRIPTION',
  },
  {
    href: '/billing',
    label: 'Billing',
    description: 'Subscription and payments',
    icon: CreditCard,
    badge: 'Soon',
    labelKey: 'SIDEBAR_BILLING_LABEL',
    descKey: 'SIDEBAR_BILLING_DESCRIPTION',
  },
  {
    href: '/knowledgebase',
    label: 'Knowledgebase',
    description: 'Company documents and data',
    icon: BookOpen,
    badge: 'Soon',
    labelKey: 'SIDEBAR_KNOWLEDGEBASE_LABEL',
    descKey: 'SIDEBAR_KNOWLEDGEBASE_DESCRIPTION',
  },
];

export default function SettingsLayout() {
  const { pathname } = useLocation();

  // Extract base settings path
  const pathParts = pathname.split('/');
  const settingsIndex = pathParts.indexOf('settings');
  const basePath = pathParts.slice(0, settingsIndex + 1).join('/');
  const currentSection = pathParts[settingsIndex + 1] || '';

  const { t } = useTranslation();

  const isActive = (href: string) => {
    const sectionPath = href.replace('/', '');
    if (sectionPath === 'general') {
      return currentSection === '' || currentSection === 'general';
    }
    return currentSection === sectionPath;
  };

  const isIndex = currentSection === '';

  return (
    <div className="max-w-6xl mx-auto">
      {/* Header with Logo — hidden on mobile section pages so the OrgTopbar
          can carry the section context instead. */}
      <div className={cn('mb-8', !isIndex && 'hidden md:block')}>
        <div className="flex items-center gap-4 mb-2">
          <img
            src="/Selise Legal Templates.svg"
            alt="SELISE Legal Templates"
            className="h-12 w-auto hidden md:block"
          />
          <div>
            <h1 className="text-xl md:text-2xl font-bold text-[hsl(var(--fg))] font-heading">
              {t('ORGSETTINGS_TITLE')}
            </h1>
            <p className="text-globe-grey text-sm md:text-base">{t('ORGSETTINGS_DESCRIPTION')}</p>
          </div>
        </div>
      </div>

      <div className="flex gap-8">
        {/* Side Navigation — full-width nav list on mobile-at-index,
            hidden on mobile section pages, fixed 256px on desktop. */}
        <aside className={cn('w-full md:w-64 shrink-0', !isIndex && 'hidden md:block')}>
          <nav className="sticky top-6 space-y-1">
            {settingsNavItems.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.href);
              const isDisabled = !!item.badge;

              if (isDisabled) {
                return (
                  <div
                    key={item.href || 'general'}
                    className="flex items-center gap-3 px-3 py-3 rounded-lg text-[hsl(var(--globe-grey))]/50 cursor-not-allowed"
                  >
                    <Icon className="h-5 w-5 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-sm">
                          {t('ORGSETTINGS_' + item.labelKey)}
                        </span>
                        <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-muted text-globe-grey">
                          {item.badge}
                        </span>
                      </div>
                      <p className="text-xs truncate opacity-60">
                        {t('ORGSETTINGS_' + item.descKey)}
                      </p>
                    </div>
                  </div>
                );
              }

              return (
                <Link
                  key={item.href || 'general'}
                  to={`${basePath}${item.href}`}
                  className={cn(
                    'flex items-center gap-3 px-3 py-3 rounded-lg transition-all group',
                    active
                      ? 'bg-selise-blue/10 text-selise-blue'
                      : 'text-globe-grey hover:bg-muted hover:text-fg'
                  )}
                >
                  <Icon className="h-5 w-5 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <span className="font-medium text-sm block">
                      {t('ORGSETTINGS_' + item.labelKey)}
                    </span>
                    <p
                      className={cn(
                        'text-xs truncate',
                        active ? 'text-selise-blue/70' : 'opacity-60'
                      )}
                    >
                      {t('ORGSETTINGS_' + item.descKey)}
                    </p>
                  </div>
                  <ChevronRight
                    className={cn(
                      'h-4 w-4 shrink-0 transition-transform',
                      active ? 'opacity-100' : 'opacity-60 md:opacity-0 md:group-hover:opacity-50'
                    )}
                  />
                </Link>
              );
            })}

            {/* Danger Zone - separated */}
            <div className="pt-4 mt-4 border-t border-border">
              <Link
                to={`${basePath}/danger-zone`}
                className={cn(
                  'flex items-center gap-3 px-3 py-3 rounded-lg transition-all group',
                  currentSection === 'danger-zone'
                    ? 'bg-crimson/10 text-crimson'
                    : 'text-globe-grey hover:bg-crimson/5 hover:text-crimson'
                )}
              >
                <AlertTriangle className="h-5 w-5 shrink-0" />
                <div className="flex-1 min-w-0">
                  <span className="font-medium text-sm block">
                    {t('ORGSETTINGS_SIDEBAR_DANGER-ZONE_LABEL')}
                  </span>
                  <p
                    className={cn(
                      'text-xs truncate',
                      currentSection === 'danger-zone' ? 'text-crimson/70' : 'opacity-60'
                    )}
                  >
                    {t('ORGSETTINGS_SIDEBAR_DANGER-ZONE_DESCRIPTION')}
                  </p>
                </div>
                <ChevronRight
                  className={cn(
                    'h-4 w-4 shrink-0 transition-transform',
                    currentSection === 'danger-zone'
                      ? 'opacity-100'
                      : 'opacity-60 md:opacity-0 md:group-hover:opacity-50'
                  )}
                />
              </Link>
            </div>
          </nav>
        </aside>

        {/* Main Content — hidden on mobile-at-list-view (so the nav list
            occupies the full width). On mobile section pages a back row
            sits above the Outlet. */}
        <main className={cn('flex-1 min-w-0', isIndex && 'hidden md:block')}>
          {!isIndex && (
            <Link
              to={basePath}
              className="md:hidden mb-4 inline-flex items-center gap-2 text-sm font-medium text-globe-grey hover:text-fg"
            >
              <ChevronLeft className="h-4 w-4" />
              {t('ORGSETTINGS_BACK_TO_SETTINGS', 'Back to settings')}
            </Link>
          )}
          <Outlet />
        </main>
      </div>
    </div>
  );
}
